import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useConfirm } from "@/components/ConfirmDialogProvider";
import { API_BASE_URL } from "@/lib/apiBase";
import { toastError, toastSuccess } from "@/lib/toast";
import { toastEntitlementOrError } from "@/lib/entitlementToast";
import { useOptionalDmDrawer } from "@/scripts/DmDrawerContext";

/**
 * Personal Circatron installs (owner-managed). Requires modules.connectors,
 * org personal_bots policy, and org.personal_bots capability to create.
 */
const CircatronMyBotsSection = ({
  orgId,
  accessToken,
  /** When true, show admin note; create still gated by API policy+capability. */
  isIntegrationsAdmin = false,
}) => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const dmDrawer = useOptionalDmDrawer();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [messagingId, setMessagingId] = useState(null);
  const [name, setName] = useState("My bot");
  const [mine, setMine] = useState([]);
  const [policy, setPolicy] = useState({ enabled: false, max_per_user: 1 });
  const [canCreate, setCanCreate] = useState(false);
  const [revealedToken, setRevealedToken] = useState(null);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  const loadMine = useCallback(async () => {
    if (!orgId || !accessToken) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/org/${orgId}/connectors/installations/mine`,
        { headers: authHeaders() }
      );
      setMine(
        Array.isArray(res.data?.installations) ? res.data.installations : []
      );
      setPolicy(
        res.data?.personal_bots || { enabled: false, max_per_user: 1 }
      );
      setCanCreate(Boolean(res.data?.can_create));
    } catch (err) {
      console.error("Failed to load personal bots", err);
      setMine([]);
      setCanCreate(false);
    } finally {
      setLoading(false);
    }
  }, [orgId, accessToken, authHeaders]);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  const active = mine.filter(
    (i) => String(i.status || "") === "active" && !i.revoked_at
  );

  const createPersonal = async (e) => {
    e?.preventDefault?.();
    setCreating(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/org/${orgId}/connectors/installations`,
        {
          provider_id: "echo",
          pack_id: "echo-notify",
          install_kind: "personal",
          name: name.trim() || "My bot",
          scopes: ["channel.post"],
        },
        { headers: authHeaders() }
      );
      setRevealedToken(res.data?.bot_token || null);
      toastSuccess("Personal bot created — copy the token now");
      await loadMine();
    } catch (err) {
      toastEntitlementOrError(err, "Failed to create personal bot", {
        canManageSettings: isIntegrationsAdmin,
        orgId,
      });
    } finally {
      setCreating(false);
    }
  };

  const rotateToken = async (inst) => {
    const ok = await confirm({
      title: "Rotate bot token?",
      description:
        "The current token stops working immediately. The new token is shown only once.",
      confirmLabel: "Rotate token",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      const res = await axios.post(
        `${API_BASE_URL}/org/${orgId}/connectors/installations/${inst.installation_id}/rotate-bot-token`,
        {},
        { headers: authHeaders() }
      );
      setRevealedToken(res.data?.bot_token || null);
      toastSuccess("Bot token rotated — copy it now");
    } catch (err) {
      toastEntitlementOrError(err, "Failed to rotate token", {
        canManageSettings: isIntegrationsAdmin,
        orgId,
      });
    }
  };

  const revoke = async (inst) => {
    const ok = await confirm({
      title: "Revoke personal bot?",
      description: `“${inst.bot?.display_name || inst.provider_id}” will stop working. This cannot be undone.`,
      confirmLabel: "Revoke",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/org/${orgId}/connectors/installations/${inst.installation_id}`,
        { headers: authHeaders() }
      );
      toastSuccess("Personal bot revoked");
      setRevealedToken(null);
      await loadMine();
    } catch (err) {
      toastEntitlementOrError(err, "Failed to revoke bot", {
        canManageSettings: isIntegrationsAdmin,
        orgId,
      });
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toastSuccess(`${label} copied`);
    } catch {
      toastError(null, `Could not copy ${label}`);
    }
  };

  const messageBot = async (inst) => {
    if (!orgId || !accessToken || messagingId != null) return;
    setMessagingId(inst.installation_id);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/org/${orgId}/connectors/installations/${inst.installation_id}/dm`,
        {},
        { headers: authHeaders() }
      );
      const conversationId = res.data?.conversation_id;
      if (!conversationId) {
        throw new Error("No conversation returned");
      }
      const peer =
        res.data?.peer ||
        (res.data?.bot
          ? {
              user_id: res.data.bot.platform_user_id,
              first_name: res.data.bot.display_name || "Bot",
              last_name: "",
              is_bot: true,
              member_avatar_url: res.data.bot.avatar_url || null,
            }
          : null);
      if (dmDrawer?.openConversation) {
        dmDrawer.openConversation(conversationId, { peer });
      } else {
        navigate(
          `/o/${encodeURIComponent(orgId)}/messages/${conversationId}`
        );
      }
    } catch (err) {
      toastEntitlementOrError(err, "Could not open bot DM", {
        canManageSettings: isIntegrationsAdmin,
        orgId,
      });
    } finally {
      setMessagingId(null);
    }
  };

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">My bots</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Personal Circatron installs you own. Message your bot in a private DM;
          channel bind for personal bots is still later. Bot tokens work with
          /bot/v1/dm.
        </p>
      </div>

      {!policy.enabled ? (
        <p className="text-sm text-muted-foreground rounded-lg border border-border/80 bg-muted/30 px-4 py-3">
          Personal bots are turned off for this organization.
          {isIntegrationsAdmin
            ? " Enable them in the Circatrons policy toggle above."
            : " Ask an admin to enable personal bots and grant org.personal_bots."}
        </p>
      ) : null}

      {revealedToken ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm space-y-2">
          <p className="font-medium text-foreground">Bot token (shown once)</p>
          <code className="block break-all text-xs">{revealedToken}</code>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => copyText(revealedToken, "Bot token")}
          >
            Copy bot token
          </Button>
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          {active.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No personal bots yet.
            </p>
          ) : (
            <ul className="space-y-3">
              {active.map((inst) => (
                <li
                  key={inst.installation_id}
                  className="rounded-lg border border-border/80 px-4 py-3 flex flex-wrap items-center justify-between gap-3"
                >
                  <div>
                    <p className="text-sm font-medium">
                      {inst.bot?.display_name || inst.provider_id}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {inst.provider_id}
                      {inst.pack_id ? ` · ${inst.pack_id}` : ""}
                      {" · "}
                      {inst.scopes?.join(", ") || "channel.post"}
                      {" · DM"}
                      {!inst.conversation_id ? " · no bound channel" : ""}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => messageBot(inst)}
                      disabled={messagingId === inst.installation_id}
                    >
                      {messagingId === inst.installation_id
                        ? "Opening…"
                        : "Message"}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => rotateToken(inst)}
                    >
                      Rotate token
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      onClick={() => revoke(inst)}
                    >
                      Revoke
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {canCreate ? (
            <form
              onSubmit={createPersonal}
              className="rounded-lg border border-border/80 px-4 py-3 space-y-3"
            >
              <p className="text-sm font-medium">Create personal Echo bot</p>
              <div className="space-y-1.5 max-w-sm">
                <Label htmlFor="personal-bot-name">Display name</Label>
                <Input
                  id="personal-bot-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={80}
                />
              </div>
              <Button type="submit" size="sm" disabled={creating}>
                {creating ? "Creating…" : "Create personal bot"}
              </Button>
            </form>
          ) : policy.enabled && active.length >= (policy.max_per_user || 1) ? (
            <p className="text-sm text-muted-foreground">
              You already have the maximum personal bots (
              {policy.max_per_user}) for this organization.
            </p>
          ) : policy.enabled ? (
            <p className="text-sm text-muted-foreground">
              You need the org.personal_bots capability to create a personal
              bot.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
};

export default CircatronMyBotsSection;
